import { filterCatalogs, filterCatalogsByReg, filterItems, filterSellers } from './sorting';
import { getCatalogCardDatas, getSellerCardDatas } from './cardData';
import { getItemCardModels, getItemIdsForCatalog, getItemIdsForSeller, getItemsForCatalog } from './itemCardModel';
import { getAllCurrentUserCatalogRegistrationIds } from './catalogRegistration';
import { getCatalogIdsForSeller } from './catalog';
import { getFollowedSellerIds } from './followSeller';
import { getLiveCatalogData } from '@/redux/modules/console';
import { getMyBidItemIds } from './bidderBiddingInfoAll';
import { getSavedSearchItemIds } from './savedSearch';
import { getStorefrontItemIds } from './storefrontPage';

export const getNextCatalogItemIds = (state: any, catalogId: number, itemId: number, count: number) => {
    const items = getItemsForCatalog(state, catalogId, { sort: 'LOT_NUMBER_LOWEST_FIRST' });
    const [theItem] = items.filter((item) => item.itemId === itemId);

    let returnItems: any = items.filter((item) => {
        return item.index > theItem?.index;
    });

    if (returnItems.length < count) {
        return [...returnItems, ...items.slice(0, count - returnItems.length)].map(({ itemId }) => itemId);
    } else {
        return returnItems.slice(0, count).map(({ itemId }) => itemId);
    }
};

export const getCatalogContainsBuyNowLots = (state: any, catalogId: number) => {
    const ids = getItemIdsForCatalog(state, catalogId);
    const all = getItemCardModels(state, ids);
    const buyNowItems = all.filter((x) => x.buyNowStatus === 1).length > 0;
    return buyNowItems;
};

type GetFollowedSellersParams = {
    count?: number | 'all';
    page?: number;
    sort?: string;
    terms?: string;
};
export const getFollowedSellers = (state: any, { count, page, sort, terms }: GetFollowedSellersParams = {}) => {
    const ids = getFollowedSellerIds(state);
    const all = getSellerCardDatas(state, ids);
    return filterSellers(all, { count, page, sort, terms });
};

type GetMyBidsParams = {
    count?: number | 'all';
    filter?: string;
    page?: number;
    sort?: string;
    tab?: string;
    terms?: string;
};

export const getMyBids = (state: any, { count, filter, page, sort, tab, terms }: GetMyBidsParams = {}) => {
    const { allBidItemIds, pastBidItemIds, upcomingBidItemIds } = getMyBidItemIds(state);
    const ids = tab === 'upcoming' ? upcomingBidItemIds : tab === 'ended' ? pastBidItemIds : allBidItemIds;
    const cardData = getItemCardModels(state, ids);
    let filteredItems;
    if (sort === 'TIME_SOONEST_FIRST') {
        filteredItems = stackSortedLiveSavedItems(state, cardData, { count, filter, page, sort, terms });
    } else {
        filteredItems = filterItems(cardData, { count, filter, page, sort, terms });
    }
    return filteredItems;
};

type StackSortedLiveSavedItemsParams = {
    count?: number | 'all';
    filter?: string;
    page?: number;
    sort?: string;
    tab?: string;
    terms?: string;
};
const stackSortedLiveSavedItems = (
    state: any,
    all,
    { count, filter, page, sort, tab, terms }: StackSortedLiveSavedItemsParams
) => {
    const liveItems = [];
    const savedItems = [];
    const pastItems = [];
    all.forEach((item) => {
        const { currentItemIndex } = getLiveCatalogData(state, item.catalogId);
        const lotsAway = item.index - currentItemIndex;
        if (currentItemIndex && lotsAway > -1) {
            item.lotsAway = lotsAway;
            liveItems.push(item);
        } else if (!item.isAvailable) {
            pastItems.push(item);
        } else {
            savedItems.push(item);
        }
    });
    const sortedLiveItems = filterItems(liveItems, { sort: 'LOTS_AWAY_FIRST' });
    const sortedPastItems = filterItems(pastItems, { sort: 'TIME_ENDING_FURTHEST' });
    const sortedSavedItems = filterItems(savedItems, { sort });
    return filterItems([...sortedLiveItems.items, ...sortedSavedItems.items, ...sortedPastItems.items], {
        count,
        filter,
        page,
        tab,
        terms,
    });
};

type GetMySavedSearchItemsParams = {
    count?: number | 'all';
    page?: number;
    sort?: string;
    terms?: string;
};

export const getMySavedSearchItemIds = (state: any, { count, page, sort, terms }: GetMySavedSearchItemsParams = {}) => {
    const ids = getSavedSearchItemIds(state);
    const all = getItemCardModels(state, ids);
    const results = filterItems(all, { count, page, sort, terms });
    return results.itemIds.length && results.itemIds.length === ids.length ? results.itemIds : ids;
};

type GetSellerCatalogsParams = {
    count?: number | 'all';
    page?: number;
    sort?: string;
    tab?: string;
    terms?: string;
};
export const getSellerCatalogs = (
    state: any,
    sellerId: number,
    { count, page, sort, tab, terms }: GetSellerCatalogsParams = {}
) => {
    const ids = getCatalogIdsForSeller(state, sellerId);
    const all = getCatalogCardDatas(state, ids);
    return filterCatalogs(all, { count, page, sort, tab, terms });
};

type GetStorefrontItemsParams = {
    buyNow?: boolean;
    count?: number | 'all';
    page?: number;
    sort?: string;
    terms?: string;
};
export const getStorefrontItems = (state: any, { buyNow, count, page, sort, terms }: GetStorefrontItemsParams = {}) => {
    const ids = getStorefrontItemIds(state);
    const all = getItemCardModels(state, ids);
    const filterBuyNow = buyNow ? all.filter((x) => x.buyNowStatus === 1) : all;
    return filterItems(filterBuyNow, { count, page, sort, terms });
};

type GetMyAuctionsParams = {
    count?: number | 'all';
    page?: number;
    sort?: string;
    tab?: string;
    terms?: string;
};
export const getMyAuctions = (state: any, { count, page, sort, tab, terms }: GetMyAuctionsParams = {}) => {
    const catalogIds = getAllCurrentUserCatalogRegistrationIds(state);
    const all = getCatalogCardDatas(state, catalogIds);
    return filterCatalogsByReg(state, all, { count, page, sort, tab, terms });
};

export const getMostPopularItemsByBids = (state: any, sellerId: number) => {
    const ids = getItemIdsForSeller(state, sellerId);
    const all = getItemCardModels(state, ids);
    const { itemIds } = filterItems(all, { count: 20, page: 1, sort: 'MOST_BIDS', tab: 'upcoming' });
    return itemIds || [];
};

export const getCatalogsForFollowedSellers = (state: any): number[] => {
    const followedSellerIds = getFollowedSellerIds(state);
    let followedSellerCatalogs = [];
    let followedSellerCatalogIds = [];
    // limiting the number of items per seller to keep data small
    followedSellerIds.forEach((id) => {
        const liveCatalogs = getSellerCatalogs(state, id, {
            count: 20,
            page: 1,
            sort: 'DATE_ASCENDING',
            tab: 'live',
        });
        if (liveCatalogs && liveCatalogs.items && liveCatalogs.items.length > 0) {
            followedSellerCatalogs = followedSellerCatalogs.concat(liveCatalogs.items);
            liveCatalogs.items.forEach((item) => {
                if (item && item.catalog && item.catalog.catalogId) {
                    followedSellerCatalogIds = followedSellerCatalogIds.concat(item.catalog.catalogId);
                }
            });
        }
    });

    return followedSellerCatalogIds.slice(0, 16);
};
